// Phone browsers throttle a page that is minimized or locked, and when it comes back the video
// sent to others can stay frozen while the person's own preview and the audio look fine, so they
// have no reason to act. Turning the camera off and on starts a fresh video stream.

const AWAY_MS = 2000; // a quick glance at a notification shouldn't flicker the tutor's view
const RESUME_DELAY_MS = 500;
const RESTART_TIMEOUT_MS = 5000;

export const isMobileBrowser = () =>
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
    || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1); // iPadOS reports as a Mac

// Returns the cleanup function. Does nothing on desktop, where hiding a tab doesn't freeze video.
export function attachMeetingRecovery(api) {
    if (!isMobileBrowser()) return () => {};

    let joined = false;
    let hiddenAt = null;
    let restarting = false;
    let restartTimer = null;
    let resumeTimer = null;
    let wakeLock = null;

    const keepScreenAwake = async () => {
        if (!joined || document.hidden || wakeLock || !navigator.wakeLock) return;
        try {
            const lock = await navigator.wakeLock.request("screen");
            wakeLock = lock;
            lock.addEventListener?.("release", () => { wakeLock = null; });
        } catch (e) {
            // refused (e.g. low battery): the call still works, the screen may just sleep
        }
    };

    const releaseScreen = () => {
        wakeLock?.release?.().catch?.(() => {});
        wakeLock = null;
    };

    const restartCamera = () => {
        if (restarting || !joined) return;
        api.isVideoMuted().then((muted) => {
            // the student's own choice to have the camera off is left alone
            if (muted || restarting || !joined) return;
            restarting = true;
            restartTimer = setTimeout(() => { restarting = false; }, RESTART_TIMEOUT_MS);
            api.executeCommand("toggleVideo");
        }).catch(() => {});
    };

    // Waiting for the "off" event rather than a fixed delay, so the two toggles can't land out of step
    // and leave the camera off.
    const onVideoMuteChanged = ({ muted }) => {
        if (!restarting || !muted) return;
        restarting = false;
        clearTimeout(restartTimer);
        api.executeCommand("toggleVideo");
    };

    const onVisibilityChange = () => {
        if (document.hidden) {
            hiddenAt = Date.now();
            clearTimeout(resumeTimer);
            return;
        }
        keepScreenAwake();
        const away = hiddenAt !== null && Date.now() - hiddenAt >= AWAY_MS;
        hiddenAt = null;
        if (away) resumeTimer = setTimeout(restartCamera, RESUME_DELAY_MS);
    };

    const onJoined = () => { joined = true; keepScreenAwake(); };
    const onLeft = () => { joined = false; releaseScreen(); };

    api.addListener("videoConferenceJoined", onJoined);
    api.addListener("videoConferenceLeft", onLeft);
    api.addListener("videoMuteStatusChanged", onVideoMuteChanged);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
        clearTimeout(restartTimer);
        clearTimeout(resumeTimer);
        document.removeEventListener("visibilitychange", onVisibilityChange);
        api.removeListener?.("videoConferenceJoined", onJoined);
        api.removeListener?.("videoConferenceLeft", onLeft);
        api.removeListener?.("videoMuteStatusChanged", onVideoMuteChanged);
        releaseScreen();
    };
}
